import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Autocomplete,
    TextField,
    CircularProgress
} from '@mui/material';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Iconify } from '../index';
import { getPracticeTreatmentList } from '../../redux/slices/practiceTreatment';
import { setCurrentTreatmentDataGlobal } from '../../redux/slices/multiTreatment';
import { addTreatment, followUpSlice } from '../../redux/slices/lead';
import { updateError } from '../../redux/slices/user';
import { updateLeadDataCount } from '../../redux/slices/whatsapp';
import { resetWhatsappChat, fetchWhatsappChatAndOtherMetaData, saveWhatsappChatMetaData } from '../../redux/slices/whatsappV2';
import groupedTreatments from '../../utils/groupedTreatments';

/**
 * TreatmentSelectionAlert - Displays an alert when no treatment is selected
 * with an inline dropdown for quick treatment selection.
 *
 * This component:
 * 1. Shows a warning when no treatment exists
 * 2. Provides inline dropdown for treatment selection
 * 3. Calls API to add treatment to lead
 * 4. Refreshes lead data and WhatsApp after successful addition
 */
const TreatmentSelectionAlert = ({ leadId, leadData, onTreatmentSelect }) => {
    const dispatch = useDispatch();
    const [treatmentOptions, setTreatmentOptions] = useState([]);
    const [selectedTreatment, setSelectedTreatment] = useState(null);
    const [isAdding, setIsAdding] = useState(false);

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { treatmentsList, isLoading } = useSelector(
        (state) => ({
            treatmentsList: state.practiceTreatment?.treatmentsList,
            isLoading: state.practiceTreatment?.isLoading
        }),
        shallowEqual
    );

    // Fetch treatments on mount
    useEffect(() => {
        if (practiceId) {
            dispatch(getPracticeTreatmentList(practiceId, { limit: 100, offset: 0 }));
        }
    }, [dispatch, practiceId]);

    // Process treatments when loaded
    useEffect(() => {
        if (treatmentsList?.results?.length > 0) {
            const grouped = groupedTreatments(treatmentsList.results);
            setTreatmentOptions(grouped);
        }
    }, [treatmentsList]);

    // Handle treatment selection
    const handleTreatmentSelect = (event, value) => {
        if (!value || isAdding) return;

        const currentLeadId = leadId || leadData?.id;
        if (!currentLeadId || !practiceId) {
            dispatch(updateError({ message: 'Unable to add treatment. Missing lead or practice information.', success: false }));
            return;
        }

        setIsAdding(true);

        // Prepare treatment detail for API
        const treatmentDetail = {
            treatment: value.name,
            custom_price: value.price || 0,
            is_practice_notification_required: false
        };

        // Callback after treatment is added
        const handleAddComplete = (status, id, treatmentId) => {
            setIsAdding(false);

            if (status === 'finish' || treatmentId) {
                setSelectedTreatment(value);

                // Update global treatment state
                dispatch(setCurrentTreatmentDataGlobal({
                    lead_id: currentLeadId,
                    first_name: leadData?.first_name || '',
                    last_name: leadData?.last_name || '',
                    treatmentData: {
                        id: treatmentId || value.id,
                        treatment: value.name,
                        price: value.price,
                        treatment_item_id: value.treatment_item_id,
                        category: value.category
                    }
                }));

                // Refresh lead data
                dispatch(followUpSlice(currentLeadId, practiceId, 'LeadDetail'));

                // Trigger treatment data refresh in OpenContactLeadPopup
                dispatch(updateLeadDataCount({ hasUpdated: true }));

                // Refresh WhatsApp chat metadata
                const phoneNumber = leadData?.lead_contact?.phone || leadData?.phone;
                if (phoneNumber) {
                    dispatch(resetWhatsappChat());
                    setTimeout(() => {
                        dispatch(saveWhatsappChatMetaData({
                            leadId: currentLeadId,
                            phoneNumber: phoneNumber?.trim().replaceAll(' ', '').replaceAll('+', ''),
                            name: `${leadData?.first_name || ''} ${leadData?.last_name || ''}`.trim() || phoneNumber,
                            avatar: null,
                        }));
                        dispatch(fetchWhatsappChatAndOtherMetaData());
                    }, 300);
                }

                dispatch(updateError({ message: 'Treatment added successfully', success: true }));

                // Call optional callback
                if (onTreatmentSelect) {
                    onTreatmentSelect(value);
                }
            }
        };

        // Call API to add treatment
        dispatch(addTreatment(treatmentDetail, practiceId, currentLeadId, handleAddComplete));
    };

    // Don't render if treatment is already selected
    if (selectedTreatment) {
        return null;
    }

    return (
        <Box sx={styles.alertCard}>
            <Box sx={styles.contentWrapper}>
                <Iconify
                    icon="solar:medical-kit-bold-duotone"
                    width={22}
                    height={22}
                    sx={{ color: '#E65100', flexShrink: 0 }}
                />
                <Typography variant="body2" sx={styles.alertText}>
                    No treatment selected
                </Typography>
                <Autocomplete
                    size="small"
                    sx={styles.autocomplete}
                    options={treatmentOptions}
                    getOptionLabel={(option) => option.name || ''}
                    groupBy={(option) => option.firstLetter}
                    loading={isLoading || isAdding}
                    disabled={isAdding}
                    onChange={handleTreatmentSelect}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={isAdding ? "Adding..." : "Select treatment"}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(isLoading || isAdding) ? (
                                            <CircularProgress color="inherit" size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <Typography variant="body2">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.category}
                                </Typography>
                            </Box>
                        </li>
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    noOptionsText="No treatments available"
                />
            </Box>
        </Box>
    );
};

const styles = {
    alertCard: {
        py: 1.5,
        px: 3,
        mb: 0,
        mx: 'auto',
        maxWidth: '560px',
        width: '100%',
        bgcolor: '#FFF7ED',
        border: '1px solid #FFE0B2',
        borderRadius: 2,
        boxShadow: '0 1px 4px rgba(255, 152, 0, 0.08)',
    },
    contentWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
    },
    alertText: {
        fontSize: '13px',
        fontWeight: 600,
        color: '#E65100',
        whiteSpace: 'nowrap',
    },
    autocomplete: {
        flex: 1,
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
            bgcolor: 'white',
            height: '36px',
            fontSize: '13px',
            borderRadius: '8px',
            '& fieldset': {
                borderColor: '#FFE0B2',
            },
            '&:hover fieldset': {
                borderColor: '#FFB74D',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#F57C00',
                borderWidth: '1.5px',
            },
        },
    },
};

TreatmentSelectionAlert.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadData: PropTypes.object,
    onTreatmentSelect: PropTypes.func
};

TreatmentSelectionAlert.defaultProps = {
    leadId: null,
    leadData: null,
    onTreatmentSelect: null
};

export default TreatmentSelectionAlert;
